import React, { memo, useEffect, useState } from 'react';
import { Button, message, Steps, theme, Typography, InputNumber, Card, Alert } from 'antd';
import { Input, Select, Table } from 'antd';
import UsersStep, { validateParticipantCount } from '@/components/quiz-wizard/UsersStep';
import { AnimatePresence, motion } from 'framer-motion';
import { useCreateChallenge } from '../../hooks/attachments/useCreateChallenge';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { getKeycloakRealm } from '@/components/helpers';
import { useTranslation } from 'react-i18next';
import { useGetChallengeUsers } from '@/hooks/users/useGetChallengeUsers';
import { useGetCategories, useGetQuestionCountByCategory } from '@hooks';
import { ChallengeMode } from '@/types';
import { TournamentConfigValues, DEFAULT_TOURNAMENT_CONFIG } from '@/components/quiz-wizard/TournamentConfig';
import { QuizDeadlineValues, DEFAULT_QUIZ_DEADLINE } from '@/components/quiz-wizard/QuizDeadlineConfig';
const { Title, Paragraph } = Typography;



const QuizWizard = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [challengeMode, setChallengeMode] = useState<ChallengeMode>(ChallengeMode.OneTimeMatching);
  const [tournamentConfig, setTournamentConfig] = useState<TournamentConfigValues>(DEFAULT_TOURNAMENT_CONFIG);
  const [quizDeadline, setQuizDeadline] = useState<QuizDeadlineValues>(DEFAULT_QUIZ_DEADLINE);
  const createChallengeMutation = useCreateChallenge();
  const navigate = useNavigate();
  const [form, setForm] = useState<{
    title: string | undefined,
    numberOfQuestions: number | undefined,
    categoryId: string[] | undefined,
  }>({
    title: undefined,
    numberOfQuestions: undefined,
    categoryId: undefined,
  })
  
  const [maxQuestions, setMaxQuestions] = useState<number | null>(null);
  const questionsMax = useGetQuestionCountByCategory();

  // Fetch max questions when categories change
  useEffect(() => {
    if (form.categoryId && form.categoryId.length > 0) {
      questionsMax.mutate(form.categoryId, {
        onSuccess(data) {
          setMaxQuestions(data);
          // Reset number of questions if it exceeds max
          if (form.numberOfQuestions && data !== null && form.numberOfQuestions > data) {
            setForm(prev => ({ ...prev, numberOfQuestions: data }));
          }
        },
      });
    } else {
      setMaxQuestions(null);
      setForm(prev => ({ ...prev, numberOfQuestions: undefined }));
    }
  }, [form.categoryId]);


  const { data: categoriesData, isLoading: categoriesLoading } = useGetCategories();

  // Validation: categories required, numberOfQuestions required and within range
  const isCategoryValid = form.categoryId && form.categoryId.length > 0;
  const isTitleValid = !!form.title && form.title.trim().length > 0;
  const isQuestionsValid = form.numberOfQuestions !== undefined && form.numberOfQuestions > 0 && 
    (maxQuestions === null || form.numberOfQuestions <= maxQuestions);
  
  // Deadline validation: at least 1 hour or 1 day required
  const isDeadlineValid = challengeMode === ChallengeMode.KnockoutTournament
    ? (tournamentConfig.responseDeadlineDays > 0 || tournamentConfig.responseDeadlineHours > 0)
    : (quizDeadline.deadlineDays > 0 || quizDeadline.deadlineHours > 0);
  
  const isStep1Valid = isTitleValid && isCategoryValid && isQuestionsValid && isDeadlineValid;

  // Dohvati korisnike za prikaz u zadnjem koraku
  const { data: usersData } = useGetChallengeUsers();
  const selectedUsers: { id: string; firstName: string; lastName: string; email: string }[] = usersData?.items?.filter((u: any) => selectedUserIds.includes(u.id)) || [];

  const next = async () => {
    if (current === 0) {
      // Validate category and questions
      if (!isTitleValid) {
        message.error(t('pages.quizWizard.titleRequired') || 'Naslov kviza je obavezan.');
        return;
      }
      if (!isCategoryValid) {
        message.error(t('pages.quizWizard.categoryRequired') || 'Odabir kategorije je obavezan.');
        return;
      }
      if (!isQuestionsValid) {
        message.error(t('pages.quizWizard.questionsRequired') || 'Unesite valjani broj pitanja.');
        return;
      }
      // Validate deadline
      if (!isDeadlineValid) {
        message.error(t('pages.quizWizard.deadlineRequired') || 'Rok za završetak je obavezan (najmanje 1 sat ili 1 dan).');
        return;
      }
      // Validate participant count based on challenge mode
      const validation = validateParticipantCount(challengeMode, selectedUsers.length);
      if (!validation.isValid) {
        message.error(validation.errorMessage || t('pages.quizWizard.usersMustBeEven'));
        return;
      }
      setCurrent(current + 1);
    } else {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  // Inicijaliziraj steps s contentom - sada samo 2 koraka
  const stepsWithContent = [
    {
      title: t('pages.quizWizard.step1') || 'Konfiguracija',
      content: (
        <div style={{textAlign: 'left', padding: 16}}>
          <Card title={t('pages.quizWizard.quizSettings') || 'Postavke kviza'} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <Typography.Text strong>
                  {t('forms.category.title') || 'Naslov'} <span style={{ color: 'red' }}>*</span>
                </Typography.Text>
                <Input
                  placeholder={t('forms.category.title') || 'Naslov'}
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  style={{ marginTop: 8 }}
                  status={form.title !== undefined && !form.title.trim() ? 'error' : undefined}
                />
              </div>
              <div>
                <Typography.Text strong>
                  {t('pages.bankPool.categorySelect') || 'Kategorije'} <span style={{ color: 'red' }}>*</span>
                </Typography.Text>
                <Select
                  mode="multiple"
                  allowClear
                  loading={categoriesLoading}
                  placeholder={t('pages.bankPool.categorySelect')}
                  value={form.categoryId}
                  onChange={(value) => setForm((prev) => ({ ...prev, categoryId: value }))}
                  options={categoriesData?.map((c: any) => ({ value: c.id, label: c.name }))}
                  style={{ width: '100%', marginTop: 8 }}
                  status={!isCategoryValid && form.categoryId !== undefined ? 'error' : undefined}
                />
              </div>
              {form.categoryId && form.categoryId.length > 0 && (
                <div>
                  <Typography.Text strong>
                    {t('forms.category.numberOfQuestions') || 'Broj pitanja'} <span style={{ color: 'red' }}>*</span>
                  </Typography.Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <InputNumber
                      placeholder={t('forms.category.numberOfQuestions')}
                      value={form.numberOfQuestions}
                      max={maxQuestions ?? undefined}
                      min={1}
                      onChange={(value) => setForm((prev) => ({ ...prev, numberOfQuestions: value ?? undefined }))}
                      style={{ width: 200 }}
                      status={form.numberOfQuestions !== undefined && !isQuestionsValid ? 'error' : undefined}
                    />
                    {maxQuestions !== null && (
                      <Typography.Text type="secondary">
                        {t('pages.quizWizard.maxAvailable', { max: maxQuestions }) || `(max: ${maxQuestions})`}
                      </Typography.Text>
                    )}
                  </div>
                  {form.numberOfQuestions !== undefined && maxQuestions !== null && form.numberOfQuestions > maxQuestions && (
                    <Alert
                      message={t('pages.quizWizard.questionsExceedMax', { max: maxQuestions }) || `Broj pitanja ne može biti veći od ${maxQuestions}`}
                      type="error"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              )}
            </div>
          </Card>

          
          <UsersStep 
            selectedUserIds={selectedUserIds} 
            setSelectedUserIds={setSelectedUserIds}
            mode={challengeMode}
            onModeChange={setChallengeMode}
            tournamentConfig={tournamentConfig}
            onTournamentConfigChange={setTournamentConfig}
            quizDeadline={quizDeadline}
            onQuizDeadlineChange={setQuizDeadline}
          />
        </div>
      ),
    },
    {
      title: t('pages.quizWizard.step4') || 'Pregled',
      content: (
        <div style={{textAlign: 'left', flexDirection: 'column', display: 'flex', gap: 16, padding: 16}}>
          <Card title={t('pages.quizWizard.quizSummary') || 'Sažetak kviza'}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <Typography.Text strong>{t('forms.category.title') || 'Naslov'}:</Typography.Text>{' '}
                <Typography.Text>{form.title || '-'}</Typography.Text>
              </div>
              <div>
                <Typography.Text strong>{t('pages.bankPool.categorySelect') || 'Kategorije'}:</Typography.Text>{' '}
                <Typography.Text>
                  {form.categoryId?.map(id => categoriesData?.find((c: any) => c.id === id)?.name).filter(Boolean).join(', ') || '-'}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text strong>{t('forms.category.numberOfQuestions') || 'Broj pitanja'}:</Typography.Text>{' '}
                <Typography.Text>{form.numberOfQuestions || '-'}</Typography.Text>
              </div>
              <div>
                <Typography.Text strong>{t('pages.quizWizard.mode') || 'Mod'}:</Typography.Text>{' '}
                <Typography.Text>
                  {challengeMode === ChallengeMode.KnockoutTournament 
                    ? (t('pages.quizWizard.knockoutTournament') || 'Knockout turnir') 
                    : (t('pages.quizWizard.oneTimeMatching') || 'Jednokratno uparivanje')}
                </Typography.Text>
              </div>
              {challengeMode === ChallengeMode.KnockoutTournament && (
                <>
                  <div>
                    <Typography.Text strong>{t('pages.quizWizard.responseDeadline') || 'Rok po rundi'}:</Typography.Text>{' '}
                    <Typography.Text>
                      {tournamentConfig.responseDeadlineDays > 0 && `${tournamentConfig.responseDeadlineDays} ${t('quiz.days') || 'dana'}`}
                      {tournamentConfig.responseDeadlineDays > 0 && tournamentConfig.responseDeadlineHours > 0 && ' '}
                      {tournamentConfig.responseDeadlineHours > 0 && `${tournamentConfig.responseDeadlineHours} ${t('quiz.hours') || 'sati'}`}
                    </Typography.Text>
                  </div>
                </>
              )}
              {challengeMode === ChallengeMode.OneTimeMatching && (
                <div>
                  <Typography.Text strong>{t('quiz.deadline') || 'Rok za završetak'}:</Typography.Text>{' '}
                  <Typography.Text>
                    {quizDeadline.deadlineDays > 0 && `${quizDeadline.deadlineDays} ${t('quiz.days') || 'dana'}`}
                    {quizDeadline.deadlineDays > 0 && quizDeadline.deadlineHours > 0 && ' '}
                    {quizDeadline.deadlineHours > 0 && `${quizDeadline.deadlineHours} ${t('quiz.hours') || 'sati'}`}
                  </Typography.Text>
                </div>
              )}
            </div>
          </Card>
          
          <Card title={t('pages.quizWizard.selectedUsers') || 'Odabrani korisnici'}>
            <Table
              size="small"
              columns={[
                { title: t('table.firstName'), dataIndex: 'firstName', key: 'firstName' },
                { title: t('table.lastName'), dataIndex: 'lastName', key: 'lastName' },
                { title: t('table.email'), dataIndex: 'email', key: 'email' },
              ]}
              dataSource={selectedUsers}
              pagination={false}
              rowKey="id"
            />
          </Card>
        </div>
      ),
    },
  ];

  const items = stepsWithContent.map((item) => ({ key: item.title, title: item.title }));

  const contentStyle: React.CSSProperties = {
    textAlign: 'left',
    backgroundColor: token.colorBgContainer,
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
    marginTop: 16,
    overflow: 'hidden',
  };

  return (
    <>
      <Title style={{marginTop: 10, marginBottom: 0}} level={2}>{t('pages.quizWizard.title')}</Title>
      <Paragraph type="secondary">
        {t('pages.quizWizard.intro')}
      </Paragraph>
      <Steps current={current} items={items} />
      <div style={contentStyle}>
        <AnimatePresence mode="wait">
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{height: '100%'}}
          >
            {stepsWithContent[current].content}
          </motion.div>
        </AnimatePresence>
      </div>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", paddingBottom: 24 }}>
        <Button onClick={() => prev()} disabled={current <= 0}>
          {t('pages.examPeriods.prev')}
        </Button>
        {current < stepsWithContent.length - 1 && (
          <Button type="primary" onClick={() => next()} disabled={!isStep1Valid || selectedUsers.length === 0}>
            {t('pages.examPeriods.next')}
          </Button>
        )}
        {current === stepsWithContent.length - 1 && (
          <Button
            type="primary"
            loading={createChallengeMutation.isPending}
            onClick={async () => {
              if (!form.categoryId || form.categoryId.length === 0 || !form.numberOfQuestions || selectedUserIds.length === 0) {
                message.error(t('pages.quizWizard.needQuestionsAndUsers'));
                return;
              }
              const payload = {
                Id: uuidv4(),
                Quiz: { 
                  categoryIds: form.categoryId, 
                  title: form.title,
                  numberOfQuestions: form.numberOfQuestions,
                },
                UsersIds: selectedUserIds,
                Mode: challengeMode,
                DeadlineDays: challengeMode === ChallengeMode.KnockoutTournament 
                  ? tournamentConfig.responseDeadlineDays 
                  : quizDeadline.deadlineDays,
                DeadlineHours: challengeMode === ChallengeMode.KnockoutTournament 
                  ? tournamentConfig.responseDeadlineHours 
                  : quizDeadline.deadlineHours,
                TournamentConfig: challengeMode === ChallengeMode.KnockoutTournament ? {
                  thirdPlaceMatchEnabled: tournamentConfig.enableThirdPlace,
                  points1stPlace: tournamentConfig.points1st,
                  points2ndPlace: tournamentConfig.points2nd,
                  points3rdPlace: tournamentConfig.points3rd,
                  scheduledStartDate: tournamentConfig.scheduledStartDate,
                  responseDeadlineDays: tournamentConfig.responseDeadlineDays,
                  responseDeadlineHours: tournamentConfig.responseDeadlineHours,
                } : undefined,
              };
              createChallengeMutation.mutate(payload, {
                onSuccess: () => {
                  message.success(t('pages.quizWizard.saveSuccess'));
                  setTimeout(() => {
                    navigate(`/${getKeycloakRealm}/`);
                  }, 500);
                },
                onError: (e: any) => message.error(e.message || t('pages.quizWizard.saveError')),
              });
            }}
          >
            {t('forms.category.save')}
          </Button>
        )}
      </div>
    </>
  );
};

export const QuizWizardPage = memo(QuizWizard); 